import { Fragment, useMemo } from 'react';
import { seeded } from '@/lib/seeded';
import { Sparkline } from '@/components/fx/Sparkline';

/**
 * Schematic panel contents.
 *
 * Each of these reproduces a real surface from the terminal — a quote board, a
 * depth ladder, a correlation matrix, a curve, a reliability diagram — with its
 * real chrome, real column headers and real section mnemonics.
 *
 * What none of them carry is a number. Every value cell renders an em dash,
 * because these panels are not connected to a source and the product's rule is
 * that an unbound panel says so rather than showing a plausible figure. The
 * shapes are seeded walks: structure, not readings.
 */

const DASH = '—';

/* ---------------------------------------------------------------------------
 * Quote board
 * ------------------------------------------------------------------------- */

const BOARD_ROWS = [
  { mn: 'MKT', name: 'Equity indices', state: 'unbound' },
  { mn: 'FX', name: 'Cross rates', state: 'unbound' },
  { mn: 'RTS', name: 'Sovereign curves', state: 'unbound' },
  { mn: 'CMDT', name: 'Commodity complexes', state: 'unbound' },
  { mn: 'CRY', name: 'Digital assets', state: 'unbound' },
  { mn: 'MAC', name: 'Macro series', state: 'unbound' },
];

export function QuoteBoard() {
  return (
    <div className="board">
      <div className="board__head" aria-hidden="true">
        <span>Section</span>
        <span>Instrument class</span>
        <span>Structure</span>
        <span>Last</span>
        <span>Δ</span>
      </div>
      {BOARD_ROWS.map((r, i) => (
        <div className="board__row" key={r.mn} style={{ ['--i' as string]: i }}>
          <code className="board__mn">{r.mn}</code>
          <span className="board__name">{r.name}</span>
          <Sparkline seed={i * 31 + 3} height={22} className="board__spark" fill={false} />
          <span className="board__val">{DASH}</span>
          <span className="board__delta">{DASH}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Depth ladder
 * ------------------------------------------------------------------------- */

export function DepthLadder({ levels = 9 }: { levels?: number }) {
  const rows = useMemo(() => {
    const rand = seeded(4242);
    return Array.from({ length: levels }, (_, i) => {
      // Depth thickens away from the touch, which is the shape a real book has.
      const away = (i + 1) / levels;
      return {
        bid: 0.18 + away * 0.62 + rand() * 0.2,
        ask: 0.18 + away * 0.62 + rand() * 0.2,
      };
    });
  }, [levels]);

  return (
    <div className="ladder">
      <div className="ladder__head" aria-hidden="true">
        <span>Bid depth</span>
        <span>Level</span>
        <span>Ask depth</span>
      </div>
      {rows.map((r, i) => (
        <div className="ladder__row" key={i} style={{ ['--i' as string]: i }}>
          <span className="ladder__side ladder__side--bid">
            <i style={{ ['--w' as string]: `${r.bid * 100}%` }} />
          </span>
          <span className="ladder__lvl">{DASH}</span>
          <span className="ladder__side ladder__side--ask">
            <i style={{ ['--w' as string]: `${r.ask * 100}%` }} />
          </span>
        </div>
      ))}
      <p className="ladder__note">
        Level 2 needs a direct exchange feed. No free source publishes one, so this book stays
        hollow.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Correlation matrix
 * ------------------------------------------------------------------------- */

const MATRIX_KEYS = ['MKT', 'FX', 'RTS', 'CMDT', 'CRY', 'MAC'];

export function HeatMatrix() {
  const cells = useMemo(() => {
    const rand = seeded(90210);
    const n = MATRIX_KEYS.length;
    const grid: number[][] = [];
    for (let r = 0; r < n; r++) {
      grid[r] = [];
      for (let c = 0; c < n; c++) {
        if (r === c) grid[r][c] = 1;
        else if (c < r) grid[r][c] = grid[c][r];
        else grid[r][c] = rand() * 1.5 - 0.75;
      }
    }
    return grid;
  }, []);

  // Diverging ramp. Correlation is not up/down, so it never borrows the
  // delta colours — it gets its own scale, per the design system.
  const ramp = (v: number) => {
    const t = (v + 1) / 2;
    if (t < 0.5) {
      const k = t / 0.5;
      return `color-mix(in srgb, var(--wft-heat-n3) ${(1 - k) * 100}%, var(--wft-heat-0))`;
    }
    const k = (t - 0.5) / 0.5;
    return `color-mix(in srgb, var(--wft-heat-p3) ${k * 100}%, var(--wft-heat-0))`;
  };

  return (
    <div className="matrix">
      <div className="matrix__grid" style={{ ['--n' as string]: MATRIX_KEYS.length }}>
        <span />
        {MATRIX_KEYS.map((k) => (
          <code key={`c-${k}`} className="matrix__lbl">
            {k}
          </code>
        ))}
        {MATRIX_KEYS.map((rk, r) => (
          <Fragment key={`row-${rk}`}>
            <code className="matrix__lbl">{rk}</code>
            {MATRIX_KEYS.map((ck, c) => (
              <i
                key={`${rk}-${ck}`}
                className="matrix__cell"
                style={{
                  background: ramp(cells[r][c]),
                  ['--i' as string]: r * MATRIX_KEYS.length + c,
                }}
                title={`${rk} × ${ck} — not connected`}
              />
            ))}
          </Fragment>
        ))}
      </div>
      <p className="matrix__note">Diverging ramp, not the delta scale. A −0.9 is not “bad”.</p>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Yield curve
 * ------------------------------------------------------------------------- */

const TENORS = ['1M', '3M', '6M', '1Y', '2Y', '5Y', '10Y', '30Y'];

export function YieldCurve() {
  const path = useMemo(() => {
    const rand = seeded(1177);
    // A normal, slightly humped curve: steep at the front, flattening out.
    const pts = TENORS.map((_, i) => {
      const t = i / (TENORS.length - 1);
      const shape = 1 - Math.exp(-t * 3.1);
      return 62 - shape * 40 - rand() * 3;
    });
    return {
      d: pts
        .map((y, i) => `${i ? 'L' : 'M'}${(i / (TENORS.length - 1)) * 260} ${y.toFixed(1)}`)
        .join(' '),
      pts,
    };
  }, []);

  return (
    <div className="curve">
      <svg
        viewBox="0 0 260 76"
        preserveAspectRatio="none"
        aria-hidden="true"
        className="curve__svg"
      >
        {[20, 40, 60].map((y) => (
          <line key={y} x1="0" y1={y} x2="260" y2={y} className="curve__grid" />
        ))}
        <path d={path.d} className="curve__line" />
        {path.pts.map((y, i) => (
          <circle
            key={i}
            cx={(i / (TENORS.length - 1)) * 260}
            cy={y}
            r="2.4"
            className="curve__node"
            style={{ ['--i' as string]: i }}
          />
        ))}
      </svg>
      <div className="curve__axis" aria-hidden="true">
        {TENORS.map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Reliability diagram
 * ------------------------------------------------------------------------- */

export function CalibrationPlot() {
  const d = useMemo(() => {
    const rand = seeded(5150);
    const pts = Array.from({ length: 9 }, (_, i) => {
      const x = (i + 1) / 10;
      // Wanders around the diagonal — the shape of an honest calibration curve.
      const y = Math.min(0.97, Math.max(0.03, x + (rand() - 0.5) * 0.17));
      return `${i ? 'L' : 'M'}${(x * 120).toFixed(1)} ${((1 - y) * 120).toFixed(1)}`;
    });
    return pts.join(' ');
  }, []);

  return (
    <div className="calib">
      <svg viewBox="0 0 120 120" aria-hidden="true" className="calib__svg">
        <line x1="0" y1="120" x2="120" y2="0" className="calib__ref" />
        <path d={d} className="calib__line" />
      </svg>
      <div className="calib__labels" aria-hidden="true">
        <span>Predicted band</span>
        <span>Realised frequency</span>
      </div>
    </div>
  );
}
